import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { TemplateState } from "../types/templates";
import { getAllTemplateFiles, getTemplateFile } from "../data/templateLoader";

interface TemplateSelectorProps {
  onTemplateSelect: (states: TemplateState[]) => void;
  selectedTemplateId?: string;
  isDarkMode: boolean;
  isApproved?: boolean;
  isPublic?: boolean;
  showInfoBanner?: boolean;
}

const Container = styled.div<{ $isDarkMode: boolean }>`
  padding: 16px 20px;
  background: ${(props) => (props.$isDarkMode ? "#1a1a1a" : "#ffffff")};
  color: ${(props) => (props.$isDarkMode ? "#e0e0e0" : "#1a1a1a")};
`;

const InfoBanner = styled.button<{ $isDarkMode: boolean; $variant?: "info" | "success"; $expanded: boolean; $compact?: boolean }>`
  padding: ${(props) => (props.$compact ? "6px 10px" : "10px 14px")};
  border-radius: 6px;
  background: ${(props) => {
    if (props.$variant === "success") {
      return props.$isDarkMode ? "rgba(76, 175, 80, 0.08)" : "rgba(76, 175, 80, 0.05)";
    }
    return props.$isDarkMode ? "rgba(33, 150, 243, 0.08)" : "rgba(33, 150, 243, 0.05)";
  }};
  border: 1px solid ${(props) => {
    if (props.$variant === "success") {
      return props.$isDarkMode ? "rgba(76, 175, 80, 0.2)" : "rgba(76, 175, 80, 0.15)";
    }
    return props.$isDarkMode ? "rgba(33, 150, 243, 0.2)" : "rgba(33, 150, 243, 0.15)";
  }};
  color: ${(props) => (props.$variant === "success" 
    ? (props.$isDarkMode ? "#81c784" : "#2e7d32")
    : (props.$isDarkMode ? "#b0d4ff" : "#1565c0"))};
  font-size: ${(props) => (props.$compact ? "11px" : "12px")};
  line-height: 1.4;
  display: flex;
  align-items: flex-start;
  gap: 6px;
  cursor: pointer;
  width: 100%;
  text-align: left;
  transition: all 0.2s ease;
  margin-bottom: ${(props) => (props.$compact ? "0" : "20px")};

  &:hover {
    background: ${(props) => {
      if (props.$variant === "success") {
        return props.$isDarkMode ? "rgba(76, 175, 80, 0.12)" : "rgba(76, 175, 80, 0.08)";
      }
      return props.$isDarkMode ? "rgba(33, 150, 243, 0.12)" : "rgba(33, 150, 243, 0.08)";
    }};
  }
`;

const InfoIcon = styled.span`
  font-size: 14px;
  flex-shrink: 0;
  margin-top: 1px;
`;

const InfoContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const InfoTextShort = styled.span`
  font-weight: 500;
`;

const InfoTextExpanded = styled.div`
  margin-top: 4px;
  font-size: 11px;
  opacity: 0.9;
`;

const ExpandIcon = styled.span<{ $expanded: boolean }>`
  font-size: 12px;
  flex-shrink: 0;
  margin-top: 2px;
  transition: transform 0.2s ease;
  transform: ${(props) => (props.$expanded ? "rotate(90deg)" : "rotate(0deg)")};
`;

const ControlsRow = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const SearchInput = styled.input<{ $isDarkMode: boolean }>`
  flex: 1;
  min-width: 200px;
  padding: 8px 12px;
  border: 2px solid ${(props) => (props.$isDarkMode ? "#404040" : "#e1e8ed")};
  border-radius: 6px;
  background: ${(props) => (props.$isDarkMode ? "#252525" : "#ffffff")};
  color: ${(props) => (props.$isDarkMode ? "#e0e0e0" : "#1a1a1a")};
  font-size: 14px;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #2196f3;
  }

  &::placeholder {
    color: ${(props) => (props.$isDarkMode ? "#666" : "#999")};
  }
`;

const TagSelect = styled.select<{ $isDarkMode: boolean }>`
  min-width: 140px;
  padding: 8px 12px;
  border: 2px solid ${(props) => (props.$isDarkMode ? "#404040" : "#e1e8ed")};
  border-radius: 6px;
  background: ${(props) => (props.$isDarkMode ? "#252525" : "#ffffff")};
  color: ${(props) => (props.$isDarkMode ? "#e0e0e0" : "#1a1a1a")};
  font-size: 14px;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #2196f3;
  }
`;

const Section = styled.div`
  margin-bottom: 20px;
`;

const SectionTitle = styled.h4<{ $isDarkMode: boolean }>`
  margin: 0 0 12px 0;
  color: ${(props) => (props.$isDarkMode ? "#ffffff" : "#2c3e50")};
  font-size: 14px;
  font-weight: 600;
`;

const TemplateGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 12px;
  max-height: 350px;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 2px 0;
`;

const TemplateCard = styled.div<{ $isDarkMode: boolean; $selected: boolean }>`
  background: ${(props) => (props.$isDarkMode ? "#252525" : "#ffffff")};
  border: 2px solid ${(props) => {
    if (props.$selected) {
      return "#2196f3";
    }
    return props.$isDarkMode ? "#404040" : "#e1e8ed";
  }};
  border-radius: 6px;
  padding: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  overflow: visible;
  margin: 2px 0;

  &:hover {
    border: 2px solid #2196f3;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${(props) => (props.$isDarkMode ? "rgba(0, 0, 0, 0.3)" : "rgba(33, 150, 243, 0.15)")};
    margin-top: 0;
    margin-bottom: 4px;
  }

  ${(props) =>
    props.$selected &&
    `
    background: ${props.$isDarkMode ? "rgba(33, 150, 243, 0.1)" : "rgba(33, 150, 243, 0.05)"};
    box-shadow: 0 2px 8px ${props.$isDarkMode ? "rgba(33, 150, 243, 0.2)" : "rgba(33, 150, 243, 0.15)"};
  `}
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
`;

const CardTitle = styled.h4<{ $isDarkMode: boolean }>`
  margin: 0;
  color: ${(props) => (props.$isDarkMode ? "#ffffff" : "#2c3e50")};
  font-size: 14px;
  font-weight: 600;
  line-height: 1.3;
  word-wrap: break-word;
  flex: 1;
  min-width: 0;
`;

const VersionBadge = styled.span`
  background: #2196f3;
  color: white;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 10px;
  font-weight: 500;
  flex-shrink: 0;
`;

const Description = styled.p<{ $isDarkMode: boolean }>`
  color: ${(props) => (props.$isDarkMode ? "#b0b0b0" : "#7f8c8d")};
  font-size: 12px;
  line-height: 1.4;
  margin: 0 0 10px 0;
  word-wrap: break-word;
`;

const Stats = styled.div`
  margin-bottom: 10px;
`;

const StateCount = styled.span<{ $isDarkMode: boolean }>`
  background: ${(props) => (props.$isDarkMode ? "#2d2d2d" : "#ecf0f1")};
  color: ${(props) => (props.$isDarkMode ? "#b0b0b0" : "#2c3e50")};
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 10px;
`;

const Tag = styled.span<{ $isDarkMode: boolean }>`
  background: ${(props) => (props.$isDarkMode ? "rgba(76, 175, 80, 0.2)" : "#e8f5e8")};
  color: ${(props) => (props.$isDarkMode ? "#81c784" : "#27ae60")};
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const PreviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 3px;
  height: 32px;
  margin-top: 8px;
`;

const PreviewColor = styled.div<{ $isDarkMode: boolean }>`
  border-radius: 4px;
  border: 1px solid ${(props) => (props.$isDarkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)")};
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.1);
  }
`;

const MoreStates = styled.div<{ $isDarkMode: boolean }>`
  background: ${(props) => (props.$isDarkMode ? "#404040" : "#bdc3c7")};
  color: ${(props) => (props.$isDarkMode ? "#b0b0b0" : "white")};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
`;

const StateSelectionSection = styled.div<{ $isDarkMode: boolean }>`
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid ${(props) => (props.$isDarkMode ? "#404040" : "#e1e8ed")};
`;

const StateSelectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const StateSelectionControls = styled.div`
  display: flex;
  gap: 10px;
`;

const ControlButton = styled.button<{ $isDarkMode: boolean }>`
  padding: 8px 16px;
  border: 1px solid ${(props) => (props.$isDarkMode ? "#404040" : "#ddd")};
  border-radius: 4px;
  background: ${(props) => (props.$isDarkMode ? "#252525" : "#ffffff")};
  color: ${(props) => (props.$isDarkMode ? "#e0e0e0" : "#1a1a1a")};
  cursor: pointer;
  font-size: 13px;
  transition: all 0.2s ease;

  &:hover {
    background: ${(props) => (props.$isDarkMode ? "#2d2d2d" : "#f8f9fa")};
    border-color: #2196f3;
  }
`;

const StatesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 8px;
  max-height: 300px;
  overflow-y: auto;
  overflow-x: hidden;
`;

const StateItem = styled.div<{ $isDarkMode: boolean; $selected: boolean }>`
  display: flex;
  align-items: center;
  padding: 12px;
  background: ${(props) => (props.$isDarkMode ? "#252525" : "#ffffff")};
  border: 2px solid ${(props) => {
    if (props.$selected) {
      return "#2196f3";
    }
    return props.$isDarkMode ? "#404040" : "#e1e8ed";
  }};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #2196f3;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px ${(props) => (props.$isDarkMode ? "rgba(0, 0, 0, 0.3)" : "rgba(33, 150, 243, 0.15)")};
  }

  ${(props) =>
    props.$selected &&
    `
    background: ${props.$isDarkMode ? "rgba(33, 150, 243, 0.1)" : "rgba(33, 150, 243, 0.05)"};
  `}
`;

const StateColor = styled.div<{ $isDarkMode: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  margin-right: 12px;
  border: 2px solid ${(props) => (props.$isDarkMode ? "#2d2d2d" : "#ffffff")};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  flex-shrink: 0;
`;

const StateInfo = styled.div`
  flex: 1;
`;

const StateName = styled.div<{ $isDarkMode: boolean }>`
  font-weight: 600;
  color: ${(props) => (props.$isDarkMode ? "#ffffff" : "#2c3e50")};
  margin-bottom: 4px;
`;

const StateDetails = styled.div<{ $isDarkMode: boolean }>`
  font-size: 12px;
  color: ${(props) => (props.$isDarkMode ? "#b0b0b0" : "#7f8c8d")};
`;

const Checkbox = styled.div<{ $selected: boolean; $isDarkMode: boolean }>`
  width: 20px;
  height: 20px;
  border: 2px solid ${(props) => (props.$selected ? "#2196f3" : (props.$isDarkMode ? "#666" : "#ddd"))};
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: bold;
  color: white;
  background: ${(props) => (props.$selected ? "#2196f3" : "transparent")};
  transition: all 0.2s ease;
  flex-shrink: 0;
`;

const EmptyState = styled.div<{ $isDarkMode: boolean }>`
  text-align: center;
  padding: 40px;
  color: ${(props) => (props.$isDarkMode ? "#b0b0b0" : "#7f8c8d")};
`;

const TemplateSelector: React.FC<TemplateSelectorProps> = ({
  onTemplateSelect,
  selectedTemplateId,
  isDarkMode,
  isApproved,
  isPublic,
  showInfoBanner = true,
}) => {
  const [selectedTemplate, setSelectedTemplate] = useState<string>(
    selectedTemplateId || ""
  );
  const [selectedStates, setSelectedStates] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedTag, setSelectedTag] = useState<string>("all");
  const [isInfoExpanded, setIsInfoExpanded] = useState<boolean>(false);
  
  // Use refs to track previous values and prevent infinite loops
  const prevSelectedStatesRef = useRef<string>("");
  const prevSelectedTemplateRef = useRef<string>("");

  const templates = getAllTemplateFiles();

  // Get unique tags from all templates
  const allTags = Array.from(
    new Set(templates.flatMap((template) => template.tags || []))
  );

  // Filter templates based on search term and tag
  const filteredTemplates = templates.filter((template) => {
    const matchesSearch =
      template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTag =
      selectedTag === "all" || template.tags?.includes(selectedTag);
    return matchesSearch && matchesTag;
  });

  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplate(templateId);
    setSelectedStates(new Set()); // Reset selected states when changing template
  };

  const handleStateToggle = (stateId: string) => {
    const newSelectedStates = new Set(selectedStates);
    if (newSelectedStates.has(stateId)) {
      newSelectedStates.delete(stateId);
    } else {
      newSelectedStates.add(stateId);
    }
    setSelectedStates(newSelectedStates);
  };

  const handleSelectAll = () => {
    const template = getTemplateFile(selectedTemplate);
    if (template) {
      const allStateIds = template.states.map((state) => state.id);
      setSelectedStates(new Set(allStateIds));
    }
  };

  const handleSelectNone = () => {
    setSelectedStates(new Set());
  };

  const selectedTemplateData = getTemplateFile(selectedTemplate);

  // Update selected states whenever selection changes
  useEffect(() => {
    // Convert Set to sorted array string for comparison (stable reference check)
    const selectedStatesKey = Array.from(selectedStates).sort().join(",");
    const hasSelectionChanged = 
      selectedStatesKey !== prevSelectedStatesRef.current ||
      selectedTemplate !== prevSelectedTemplateRef.current;

    // Only call onTemplateSelect if selection actually changed
    if (hasSelectionChanged) {
      if (selectedTemplate && selectedStates.size > 0) {
        const template = getTemplateFile(selectedTemplate);
        if (template) {
          const selectedTemplateStates = template.states.filter((state) =>
            selectedStates.has(state.id)
          );
          onTemplateSelect(selectedTemplateStates);
        }
      } else {
        // Only call with empty array if we previously had a selection
        if (prevSelectedStatesRef.current !== "" || prevSelectedTemplateRef.current !== "") {
          onTemplateSelect([]);
        }
      }
      
      // Update refs to track current state
      prevSelectedStatesRef.current = selectedStatesKey;
      prevSelectedTemplateRef.current = selectedTemplate;
    }
  }, [selectedStates, selectedTemplate, onTemplateSelect]);

  const canBecomeTemplate = isApproved && isPublic;

  return (
    <Container $isDarkMode={isDarkMode}>
      {showInfoBanner && (
        <>
          {canBecomeTemplate && (
            <InfoBanner
              $isDarkMode={isDarkMode}
              $variant="success"
              $expanded={isInfoExpanded}
              onClick={() => setIsInfoExpanded(!isInfoExpanded)}
            >
              <InfoIcon>✨</InfoIcon>
              <InfoContent>
                <InfoTextShort>Your map is eligible for templates!</InfoTextShort>
                {isInfoExpanded && (
                  <InfoTextExpanded>
                    Since your map is approved and public, it can be added to the template library for others to use.
                  </InfoTextExpanded>
                )}
              </InfoContent>
              <ExpandIcon $expanded={isInfoExpanded}>▶</ExpandIcon>
            </InfoBanner>
          )}

          {!canBecomeTemplate && (
            <InfoBanner
              $isDarkMode={isDarkMode}
              $expanded={isInfoExpanded}
              onClick={() => setIsInfoExpanded(!isInfoExpanded)}
            >
              <InfoIcon>ℹ️</InfoIcon>
              <InfoContent>
                <InfoTextShort>Want your map to become a template?</InfoTextShort>
                {isInfoExpanded && (
                  <InfoTextExpanded>
                    First request approval for your map. Once approved, make it public. Approved and public maps can be added to the template library.
                  </InfoTextExpanded>
                )}
              </InfoContent>
              <ExpandIcon $expanded={isInfoExpanded}>▶</ExpandIcon>
            </InfoBanner>
          )}
        </>
      )}

      <ControlsRow>
        <SearchInput
          $isDarkMode={isDarkMode}
            type="text"
            placeholder="Search templates..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
        />
        <TagSelect
          $isDarkMode={isDarkMode}
            value={selectedTag}
            onChange={(e) => setSelectedTag(e.target.value)}
          >
            <option value="all">All Tags</option>
            {allTags.map((tag) => (
              <option key={tag} value={tag}>
                {tag.charAt(0).toUpperCase() + tag.slice(1)}
              </option>
            ))}
        </TagSelect>
      </ControlsRow>

      <Section>
        <SectionTitle $isDarkMode={isDarkMode}>Step 1: Choose Template File</SectionTitle>
        <TemplateGrid>
          {filteredTemplates.map((template) => (
            <TemplateCard
              key={template.id}
              $isDarkMode={isDarkMode}
              $selected={selectedTemplate === template.id}
              onClick={() => handleTemplateSelect(template.id)}
            >
              <CardHeader>
                <CardTitle $isDarkMode={isDarkMode}>{template.name}</CardTitle>
                <VersionBadge>{template.apiVersion}</VersionBadge>
              </CardHeader>

              <Description $isDarkMode={isDarkMode}>{template.description}</Description>

              <Stats>
                <StateCount $isDarkMode={isDarkMode}>
                  {template.states.length} state
                  {template.states.length !== 1 ? "s" : ""}
                </StateCount>
              </Stats>

              {template.tags && template.tags.length > 0 && (
                <TagsContainer>
                  {template.tags.map((tag) => (
                    <Tag key={tag} $isDarkMode={isDarkMode}>
                    {tag}
                    </Tag>
                ))}
                </TagsContainer>
              )}

              <PreviewGrid>
                  {template.states.slice(0, 4).map((state) => (
                  <PreviewColor
                      key={state.id}
                    $isDarkMode={isDarkMode}
                      style={{ backgroundColor: state.color }}
                      title={state.name}
                    />
                  ))}
                  {template.states.length > 4 && (
                  <MoreStates $isDarkMode={isDarkMode}>
                      +{template.states.length - 4}
                  </MoreStates>
                  )}
              </PreviewGrid>
            </TemplateCard>
          ))}
        </TemplateGrid>
      </Section>

      {selectedTemplateData && (
        <StateSelectionSection $isDarkMode={isDarkMode}>
          <StateSelectionHeader>
            <SectionTitle $isDarkMode={isDarkMode}>Step 2: Select States to Import</SectionTitle>
            <StateSelectionControls>
              <ControlButton $isDarkMode={isDarkMode} onClick={handleSelectAll}>
                Select All
              </ControlButton>
              <ControlButton $isDarkMode={isDarkMode} onClick={handleSelectNone}>
                Select None
              </ControlButton>
            </StateSelectionControls>
          </StateSelectionHeader>

          <StatesGrid>
            {selectedTemplateData.states.map((state) => (
              <StateItem
                key={state.id}
                $isDarkMode={isDarkMode}
                $selected={selectedStates.has(state.id)}
                onClick={() => handleStateToggle(state.id)}
              >
                <StateColor $isDarkMode={isDarkMode} style={{ backgroundColor: state.color }} />
                <StateInfo>
                  <StateName $isDarkMode={isDarkMode}>{state.name}</StateName>
                  <StateDetails $isDarkMode={isDarkMode}>
                    Capital: {state.capital} | Population:{" "}
                    {state.population.toLocaleString()}
                  </StateDetails>
                </StateInfo>
                <Checkbox
                  $selected={selectedStates.has(state.id)}
                  $isDarkMode={isDarkMode}
                >
                  {selectedStates.has(state.id) ? "✓" : ""}
                </Checkbox>
              </StateItem>
            ))}
          </StatesGrid>
        </StateSelectionSection>
      )}

      {filteredTemplates.length === 0 && (
        <EmptyState $isDarkMode={isDarkMode}>
          <p>No templates found matching your criteria.</p>
        </EmptyState>
      )}
    </Container>
  );
};

export default TemplateSelector;
